import { Button } from '@edsn/pie-components';
import AddIcon from '@iconify/icons-material-symbols/add';
import list from '@iconify/icons-material-symbols/list';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Card } from '#pie/components/card/Card';
import { ConnectionInfo } from '#pie/components/connection-info/ConnectionInfo';
import { FailureInfo } from '#pie/components/failure-info/FailureInfo';
import { Icon } from '#pie/components/icon/Icon';
import { Message } from '#pie/components/message/Message';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { TicketTableEan } from '#pie/components/ticket-table-ean/TicketTableEan';
import {
  type ProblemDetails,
  Product,
  useGetConnectionInformation,
} from '#pie/internal/api/pie-bff';
import { searchAtom } from '#pie/stores/search';

export const ConnectionInformationDetail = () => {
  const { t } = useTranslation();
  const [eanParam, setEan] = useAtom(searchAtom);
  const ean = eanParam!;

  const { data, error, isLoading } = useGetConnectionInformation({
    ean18: ean,
  });

  const getNewTicketUrl = () => {
    if (!isLoading && data) {
      const searchParams = new URLSearchParams();
      if (
        data.product === Product.Electricity ||
        data.product === Product.Both
      ) {
        searchParams.append('electricityean', ean);
      }
      if (data.product.includes(Product.Gas || data.product === Product.Both)) {
        searchParams.append('gasean', ean);
      }

      return {
        pathname: '/berichtenbox/nieuw/ean',
        search: searchParams.toString(),
      };
    }

    return '/berichtenbox/nieuw';
  };

  const problemDetails = error as ProblemDetails;
  return (
    <>
      <PageHead
        title={t('connection_information_detail.header.title', {
          connectionEan: ean,
        })}
      />
      <Page>
        <PageHeader
          title={t('connection_information_detail.header.title', {
            connectionEan: ean,
          })}
          backOnClick={() => setEan(undefined)}
          previousText={t('connection_information_detail.button.back')}
        >
          <Button
            as={Link}
            iconStart={
              <Icon
                icon={AddIcon}
                className="text-action-secondary-enabled flex items-center"
              />
            }
            isLoading={isLoading}
            to={getNewTicketUrl()}
            action="tertiary"
            emphasis="subtle"
          >
            {t('connection_information_detail.header.button.new')}
          </Button>
        </PageHeader>
      </Page>

      <Stack gap="lg" className="mx-auto my-6 max-w-4xl">
        {!isLoading &&
          (!data ? (
            <Message variant="error">
              {t('connection_information_component_error.message')}
              {problemDetails && (
                <div>
                  <Text className="text-sm">
                    {t('common.error_date')} {new Date().toISOString()}
                  </Text>
                  {problemDetails?.traceId && (
                    <Text className="text-sm">
                      TraceId: {problemDetails.traceId}
                    </Text>
                  )}
                </div>
              )}
            </Message>
          ) : !data?.deviceStatus ? (
            <>
              <Message variant="error">
                <Text>
                  {t('connection_information_detail.message.unauthorized')}
                </Text>
              </Message>
              <ConnectionInfo ean18={ean} isCollapsible={false} />
            </>
          ) : (
            <>
              <FailureInfo
                ean18={ean}
                isCollapsible={false}
                showEan={false}
                showNoFailure
              />
              <ConnectionInfo ean18={ean} isCollapsible={false} />
            </>
          ))}

        <Stack gap="sm" as="section">
          <Card
            heading={
              <div className="flex items-center gap-2">
                <Icon className="text-content-heading" icon={list} />
                <Text as="h2" variant="h6" className="text-content-heading">
                  {t('connection_information_detail.text.tickethistory')}
                </Text>
              </div>
            }
            size="md"
          >
            <Stack gap="md" divider="line">
              <TicketTableEan ean={ean} />
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </>
  );
};
